import { getPlanMetadata } from './planConfig';

const MEDICAL_QUESTIONS = [
  {
    questionNumber: 1,
    question:
      'Have you or any of your dependants sought advice or has been diagnosed or treated for any medical or surgical conditions in the past 12 months?',
    formKey: 'q1',
  },
  {
    questionNumber: 2,
    question:
      'Do you, or any of your dependants take any chronic medication at this stage or are expecting to take medicine on an ongoing basis in the near future?',
    formKey: 'q2',
  },
  {
    questionNumber: 3,
    question:
      'Are you or any of your dependants planning or reasonably expecting to be hospitalised or to have a procedure or treatment in the next 12 months?',
    formKey: 'q3',
  },
  {
    questionNumber: 4,
    question:
      'Are there any other conditions or symptoms not mentioned above for which medical advice, diagnosis, care or treatment has been recommended or received, or could potentially result in a medical claim in the next 12 months that you would like to disclose?',
    formKey: 'q4',
  },
];

const DEBIT_ORDER_LABELS = {
  '1': '1st',
  '5': '5th',
  '15': '15th',
  '25': '25th',
};

function emptyString(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === 'Yes' || value === 'yes') return 'Yes';
  if (value === false || value === 'No' || value === 'no') return 'No';
  return 'No';
}

function mapIdType(idType) {
  if (idType === 'ID Document') return 'ID';
  if (idType === 'Passport') return 'Passport';
  return emptyString(idType);
}

function mapRefundsSameAsContribution(value) {
  if (value === 'same' || value === 'Yes') return 'Yes';
  if (value === 'different' || value === 'No') return 'No';
  return 'Yes';
}

function mapDebitOrderDate(value) {
  if (!value) return '';
  return DEBIT_ORDER_LABELS[String(value)] || String(value);
}

function getContacts(formData) {
  return Array.isArray(formData.members_contacts) && formData.members_contacts.length > 0
    ? formData.members_contacts[0]
    : {};
}

function serializeConditionDetails(conditions) {
  if (!Array.isArray(conditions) || conditions.length === 0) return '';

  return conditions
    .map((cond) => {
      const parts = [
        cond.fullname,
        cond.illness,
        cond.treated ? `Treated: ${cond.treated}` : '',
        cond.firsttreatmentdate ? `First treatment: ${cond.firsttreatmentdate}` : '',
        cond.lasttreatmentdate ? `Last treatment: ${cond.lasttreatmentdate}` : '',
        cond.gpfullname ? `Doctor: ${cond.gpfullname}` : '',
        cond.medication ? `Medication: ${cond.medication}` : '',
      ].filter(Boolean);

      return parts.join('; ');
    })
    .join(' | ');
}

function mapDependant(dep) {
  return {
    idType: mapIdType(dep.d_idpass),
    idPassportNumber: emptyString(dep.d_idnum),
    firstName: emptyString(dep.d_firstname),
    surname: emptyString(dep.d_surname),
    dateOfBirth: emptyString(dep.d_dob),
    gender: emptyString(dep.d_gender_radio),
    relationship: emptyString(dep.d_rel),
    maritalStatus: emptyString(dep.d_marital),
    ethnicity: emptyString(dep.d_race),
    contactNumber: emptyString(dep.d_contact),
    alternateContactNumber: emptyString(dep.d_altcontact),
    email: emptyString(dep.d_altemail),
  };
}

function buildMedicalHistoryQuestions(formData) {
  const medicalHistory = Array.isArray(formData.medical_history) ? formData.medical_history : [];

  return MEDICAL_QUESTIONS.map(({ questionNumber, question, formKey }) => {
    const answer = formData[formKey] || 'No';
    const relatedConditions = medicalHistory.filter((cond) => cond.question === formKey);

    return {
      questionNumber,
      question,
      answer,
      details: answer === 'Yes' ? serializeConditionDetails(relatedConditions) : '',
    };
  });
}

export function mapToOnlineAppPayload(formData) {
  const contacts = getContacts(formData);
  const { selectedPlanGroup, selectedPlan } = getPlanMetadata(formData.recommendedMedicalAidPlan);
  const dependants = Array.isArray(formData.dependants) ? formData.dependants.map(mapDependant) : [];

  return {
    plan: {
      medicalAidStartDate: emptyString(formData.medstart_date),
      selectedPlanGroup,
      selectedPlan,
    },
    personalInformation: {
      idType: mapIdType(formData.idpasstype),
      idPassportNumber: emptyString(formData.mainid_number),
      title: emptyString(formData.main_title),
      surname: emptyString(formData.main_surname),
      firstName: emptyString(formData.main_firstname),
      initials: emptyString(formData.main_initial),
      maritalStatus: emptyString(formData.main_marital),
      dateOfBirth: emptyString(formData.m_dob),
      gender: emptyString(formData.mgender_radio),
      language: emptyString(formData.main_lang),
      ethnicity: emptyString(formData.m_race),
    },
    contactAndTaxInformation: {
      taxNumber: emptyString(contacts.mtax),
      cellphone: emptyString(contacts.m_cell || formData.m_cell),
      workNumber: emptyString(contacts.m_telw),
      homeNumber: emptyString(contacts.m_telh),
      email: emptyString(contacts.m_email || formData.m_email),
      alternateEmail: emptyString(contacts.m_altemail),
      preferredMethodOfCommunication: emptyString(contacts.m_comm_method),
      address: {
        isPoBox: toYesNo(formData.isPOBox),
        unitNumber: emptyString(contacts.unit_num),
        complexName: emptyString(contacts.complex_name),
        streetNumber: emptyString(contacts.m_psnum),
        streetName: emptyString(contacts.m_psname),
        suburb: emptyString(contacts.m_psuburb),
        postalCode: emptyString(contacts.m_postalcode),
      },
    },
    dependantsRegistration: {
      registerDependants: formData.registerDependants || 'No',
      dependants,
    },
    bankingDetails: {
      isThirdPartyBankAccount: toYesNo(formData.isThirdPartyAccount),
      bankName: emptyString(formData.bankName),
      branchName: emptyString(formData.branchName),
      branchCode: emptyString(formData.branchCode),
      accountHolder: emptyString(formData.accountHolder),
      accountNumber: emptyString(formData.accountNumber),
      accountType: emptyString(formData.accountType),
      refundsSameAsContribution: mapRefundsSameAsContribution(formData.refundsSameAsContribution),
      debitOrderDate: mapDebitOrderDate(formData.debitOrderDate),
    },
    medicalHistory: {
      questions: buildMedicalHistoryQuestions(formData),
    },
  };
}
